'use strict';

import LocalesConfig from '../config/languages.json';

export default angular
  .module('maestro.services.i18n', [])
  .service('I18nService', I18nService)

  .config(function(
    $translateProvider,
    $translatePartialLoaderProvider,
    webcoreProvider,
  ) {
    const localeLocalStorageKey = 'Main-Language';
    const defaultLocale = 'en';

    // Modify locale config based on environment variable
    const LANGUAGE_CODES = process?.env?.['AVAILABLE_LANGUAGES']?.split(',') || [];
    LocalesConfig.list = LocalesConfig.list.map(l => {
      if (LANGUAGE_CODES.includes(l.code)) {
        l.hidden = false;
      }
      return l;
    });

    const hash = CACHE_BUSTING_HASH; // Defined by webpack as value on global scope in webpack.config.js
    let webcore = webcoreProvider.getWebCore();
    let preservedLocale;
    try {
      preservedLocale = angular.fromJson(webcore.services.localStorage.get(localeLocalStorageKey));
    } catch (e) {}

    $translatePartialLoaderProvider.addPart('main');
    $translatePartialLoaderProvider.addPart('help');
    $translatePartialLoaderProvider.addPart('organizations');
    $translatePartialLoaderProvider.addPart('properties.lint.alert');
    $translatePartialLoaderProvider.addPart('reports');
    $translatePartialLoaderProvider.addPart('notifications');

    $translateProvider
      .useLoader('$translatePartialLoader', {
        urlTemplate: `/locales/{lang}/{part}.json?hash=${hash}`
      });
    $translateProvider.registerAvailableLanguageKeys(
      LocalesConfig.list.map(l => l.code),
      LocalesConfig.list.reduce((acc, l) => {
        acc[l.code + '-*'] = l.code;
        return acc;
      }, {}));
    $translateProvider.useSanitizeValueStrategy('sanitizeParameters');
    $translateProvider.useMessageFormatInterpolation();
    $translateProvider.preferredLanguage((preservedLocale && preservedLocale.code)
      ? preservedLocale.code
      : defaultLocale);
    $translateProvider.fallbackLanguage((LocalesConfig.default && LocalesConfig.default.code)
      ? LocalesConfig.default.code
      : defaultLocale);
  }).name;

function I18nService($translate, $log, utils, $q) {
  const localeLocalStorageKey = 'Main-Language';
  let currentLocale;
  let lngLoadingPromise;

  angular.extend(this, {
    setLocale: setLocale,
    getLocalesList: getLocalesList,
    getLocale: getLocale,
    ensureLoaded: ensureLoaded
  });

  init();

  function init() {
    const DEFAULT_LANGUAGE = process?.env?.['DEFAULT_LANGUAGE'] || 'en';

    // Trying to get previously saved locale
    let preservedLocale = utils.getStorageItem(localeLocalStorageKey);
    if (preservedLocale) {
      preservedLocale = angular.fromJson(preservedLocale);
      if (preservedLocale) {
        let loc = LocalesConfig.list.find((l) => l && l.code === preservedLocale.code);
        if (loc) {
          currentLocale = angular.copy(loc);
        }
      }
    }

    // Set initial locale
    if (!currentLocale) {
      let initialLocale = LocalesConfig.list.find((l) => l && !l.hidden && l.code === DEFAULT_LANGUAGE);
      currentLocale = initialLocale ? initialLocale : angular.copy(LocalesConfig.default);
    }

    utils.setStorageItem(localeLocalStorageKey, currentLocale);
    lngLoadingPromise = $q.when($translate.use(currentLocale.code))
      .then(
        () => lngLoadingPromise = undefined,
        (localeCode) => {
          $log.error(`Unable to use ${localeCode} locale`);
          lngLoadingPromise = undefined;
        }
      );
    moment.locale(currentLocale.moment);
  }

  function ensureLoaded() {
    if (lngLoadingPromise) {
      return $q.when(lngLoadingPromise);
    }

    return $q.resolve();
  }

  /**
   * Get list of available locales
   * @return {Array<{code: string, flag: string}>}
   */
  function getLocalesList() {
    let localesList = angular.copy(LocalesConfig.list);
    return localesList.filter(l => !l.hidden);
  }

  /**
   * Get current locale
   * @return {{code: string, flag: string}}
   */
  function getLocale() {
    return angular.copy(currentLocale);
  }

  /**
   * Set locale
   * @param {string|{code: string, flag: string}} locale locale code or locale object
   */
  function setLocale(locale) {
    if (!locale) {
      $log.error('Unable to set locale: empty locale');
      return;
    }
    if (angular.isString(locale)) {
      let loc = LocalesConfig.list.find((l) => l && l.code === locale);
      if (!loc) {
        $log.error('Unable to set locale: unknown locale: ' + locale);
        return;
      }
      currentLocale = angular.copy(loc);
    } else {
      let loc = LocalesConfig.list.find((l) => l && l.code === locale.code);
      if (!loc) {
        $log.error('Unable to set locale: unknown locale: ' + angular.toJson(locale));
        return;
      }
      currentLocale = angular.copy(loc);
    }
    lngLoadingPromise = $q.when($translate.use(currentLocale.code))
      .then(
        () => lngLoadingPromise = undefined,
        (localeCode) => {
          $log.error(`Unable to use ${localeCode} locale`);
          lngLoadingPromise = undefined;
        }
      );
    moment.locale(currentLocale.moment);
    utils.setStorageItem(localeLocalStorageKey, currentLocale);
  }

}
